import { styled } from '@mui/material';
import SupportingEvidence from 'components/Grading/SupportingEvidence';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import { forwardRef, memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary } from 'shell/components/Accordion';
import Button from 'shell/components/Button';
import Divider from 'shell/components/Divider';
import FormLabel from 'shell/components/FormLabel';
import Radio from 'shell/components/Radio';
import RadioGroup from 'shell/components/RadioGroup';
import Stack from 'shell/components/Stack';
import Switch from 'shell/components/Switch';
import Tag from 'shell/components/Tag';
import Text from 'shell/components/Text';
import TextArea from 'shell/components/TextArea';
import TextInput from 'shell/components/TextInput';
import ViewMore from 'shell/components/ViewMore';
import { isValidRate, pickOptionByRange, scrollElementIntoView } from 'utils/helper';

const GradingExplanationTextArea = memo(({ criterionTitle, value, onChange }) => {
    const { t } = useTranslation();
    return (
        <TextArea
            aria-label={`grading explanation for criterion ${criterionTitle}`}
            placeholder={t(
                'placeholder.grading-text-area',
                'Briefly explain why you chose the grade and how the student can improve; AI will polish your feedback.'
            )}
            rows={6}
            value={value}
            onChange={onChange}
        />
    );
});

const AccordionItem = memo(
    ({
        index,
        disabled,
        showGraded,
        showGradingExplanationInput,
        rubric,
        criterion,
        optionId,
        gradingExplanation,
        setForm,
        gradeOverrides,
        setGradeOverrides,
        selectedCriterionId,
        setSelectedCriterionId,
        handleCriterionNavigation,
        hideCriterionNavigation,
        locationData,
    }) => {
        const { device } = useViewport();
        const { t } = useTranslation('assignment');

        const { locations, fileNames, fileType, originalFile } = locationData;

        const onChangeOption = useCallback(
            (criterionId, optionId) => {
                setForm((current) => {
                    return {
                        ...current,
                        criteria: {
                            ...current.criteria,
                            [criterionId]: optionId,
                        },
                    };
                });
                setGradeOverrides((current) => omit(current, [criterionId, 'total']));
            },
            [setForm, setGradeOverrides]
        );

        const onChangeOverride = useCallback(
            (criterionId, value) => {
                setGradeOverrides((current) => omit({ ...current, [criterionId]: value }, ['total']));
                setForm((current) => {
                    const criterion = rubric.find(({ id }) => id === criterionId);
                    if (!criterion) return current;
                    const option = pickOptionByRange(criterion, value);
                    if (!option) return current;
                    return {
                        ...current,
                        criteria: {
                            ...current.criteria,
                            [criterionId]: option.id,
                        },
                    };
                });
            },
            [rubric, setForm, setGradeOverrides]
        );

        const onChangeExplanation = useCallback(
            (e) => {
                setForm((current) => {
                    return {
                        ...current,
                        grading_explanations: {
                            ...current.grading_explanations,
                            [criterion.id]: e.target.value,
                        },
                    };
                });
            },
            [criterion.id, setForm]
        );

        const criterionSuggestedScore = criterion.options.find(({ suggested }) => suggested)?.rate;
        const criterionSelectedScore = !optionId
            ? null
            : Number(criterion.options.find(({ id }) => id === optionId).rate);
        const criterionMaxPossibleScore = Math.max(...criterion.options.map(({ rate }) => Number(rate)));
        const doesOverrideExist = Object.keys(gradeOverrides).includes(criterion.id);
        const overridesInputValue = doesOverrideExist
            ? gradeOverrides[criterion.id]
            : (criterionSelectedScore ?? criterionSuggestedScore ?? '');

        return (
            <CustomAccordion
                id={`rubric-criterion-${index}`}
                key={criterion.id}
                expanded={selectedCriterionId === criterion.id}
                isGraded={showGraded && (Boolean(optionId) || doesOverrideExist)}
                onChange={() => {
                    setSelectedCriterionId(selectedCriterionId === criterion.id ? null : criterion.id);
                }}
            >
                <CustomAccordionSummary
                    isExpanded={selectedCriterionId === criterion.id}
                    isGraded={showGraded && (Boolean(optionId) || doesOverrideExist)}
                    role={'presentation'}
                >
                    <Stack sx={{ width: '100%' }} spacing={2}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Text weight={'medium'}>{criterion.title}</Text>
                            <Stack sx={{ flexShrink: 0 }} alignItems={'center'} direction={'row'} spacing={2}>
                                {showGraded && (Boolean(optionId) || doesOverrideExist) && (
                                    <Tag sx={{ my: -1 }} type={'general'} value={'graded'} />
                                )}
                                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                    <Text>{t('rubric.criterion-points', 'Pts')}: </Text>
                                    <OverrideTextInput
                                        aria-label={`grade override for criterion ${criterion.title}`}
                                        disabled={disabled}
                                        error={
                                            !doesOverrideExist
                                                ? false
                                                : !isValidRate(gradeOverrides[criterion.id]) ||
                                                  gradeOverrides[criterion.id] < 0 ||
                                                  gradeOverrides[criterion.id] > criterionMaxPossibleScore
                                        }
                                        isSuggestedScore={criterionSelectedScore === null && !doesOverrideExist}
                                        size={'small'}
                                        value={overridesInputValue}
                                        onClick={(e) => e.stopPropagation()}
                                        onInput={(e) => onChangeOverride(criterion.id, e.target.value)}
                                    />
                                    <Text>/ {criterionMaxPossibleScore}</Text>
                                </Stack>
                            </Stack>
                        </Stack>
                        <ViewMore variant={'p3'} text={criterion.description} />
                    </Stack>
                    {!hideCriterionNavigation && selectedCriterionId === criterion.id && (
                        <FeedbackButton variant={'secondary'} onClick={() => handleCriterionNavigation(criterion.id)}>
                            {t('assignment:feedback-section.view-feedback', 'View Feedback')}
                        </FeedbackButton>
                    )}
                </CustomAccordionSummary>
                <CustomAccordionDetails>
                    <Stack divider={<Divider />}>
                        {criterion.justification && (
                            <Stack
                                sx={(theme) => ({ p: 3, background: theme.palette.ensightful.primary[50] })}
                                spacing={2}
                            >
                                <Text>
                                    {t('rubric.suggestion-rationale', 'Suggestion rationale (instructor review only)')}
                                </Text>
                                <Text variant={'p3'}>{criterion.justification}</Text>
                                {criterion.gradingInstructions && (
                                    <GradingInstructionsAccordion>
                                        <GradingInstructionsAccordionSummary>
                                            <Text color={'inherit'}>
                                                {t(
                                                    'advanced-grading.grading-instructions-label',
                                                    'Grading Instructions'
                                                )}
                                            </Text>
                                        </GradingInstructionsAccordionSummary>
                                        <GradingInstructionsAccordionDetails>
                                            <Text rich variant={'p3'}>
                                                {criterion.gradingInstructions}
                                            </Text>
                                        </GradingInstructionsAccordionDetails>
                                    </GradingInstructionsAccordion>
                                )}
                            </Stack>
                        )}
                        <SupportingEvidence
                            locations={locations}
                            fileNames={fileNames}
                            fileType={fileType}
                            originalFile={originalFile}
                        />
                        <OverflowStack
                            aria-label={`select a grade for criterion ${index + 1}`}
                            component={RadioGroup}
                            device={device}
                            divider={<Divider flexItem orientation={device === 'mobile' ? 'horizontal' : 'vertical'} />}
                            tabIndex={0}
                        >
                            {criterion.options.map((option) => (
                                <CustomFormLabel key={option.id} id={`${option.id}-label`} htmlFor={option.id}>
                                    <Stack spacing={3}>
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Stack sx={{ minWidth: 0 }} alignItems={'flex-start'} spacing={3}>
                                                <Text data-cy={`rubric-points-${option.rate}`}>
                                                    {t('rubric.points-value', '{{points}} pts', {
                                                        points: option.rate,
                                                    })}
                                                </Text>
                                                <Text>{option.title}</Text>
                                            </Stack>
                                            <Stack sx={{ flexShrink: 0 }} direction={'row'} spacing={2}>
                                                {option.suggested && <Tag type={'general'} value={'suggested'} />}
                                                <CustomRadio
                                                    id={option.id}
                                                    checked={optionId === option.id}
                                                    disabled={disabled}
                                                    onChange={() => onChangeOption(criterion.id, option.id)}
                                                />
                                            </Stack>
                                        </Stack>
                                        <Text variant={'p3'}>{option.description}</Text>
                                    </Stack>
                                </CustomFormLabel>
                            ))}
                        </OverflowStack>
                        {showGradingExplanationInput && (
                            <Stack sx={{ p: 3 }} spacing={2}>
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <FormLabel id={`${criterion.id}-switch`}>
                                        {t('grading-section.add-grading-explanation', 'Add grading explanation for AI')}
                                    </FormLabel>
                                    <Switch
                                        aria-labelledby={`${criterion.id}-switch`}
                                        checked={typeof gradingExplanation !== 'undefined'}
                                        onChange={(_, checked) => {
                                            setForm((current) => {
                                                return {
                                                    ...current,
                                                    grading_explanations: checked
                                                        ? {
                                                              ...current.grading_explanations,
                                                              [criterion.id]: '',
                                                          }
                                                        : omit(current.grading_explanations, [criterion.id]),
                                                };
                                            });
                                        }}
                                    />
                                </Stack>
                                <Text variant={'p3'}>
                                    {t(
                                        'grading-section.ai-feedback',
                                        'Makes AI feedback more relevant to the student’s performance.'
                                    )}
                                </Text>
                                {typeof gradingExplanation !== 'undefined' && (
                                    <GradingExplanationTextArea
                                        criterionTitle={criterion.title}
                                        value={gradingExplanation ?? ''}
                                        onChange={onChangeExplanation}
                                    />
                                )}
                            </Stack>
                        )}
                    </Stack>
                </CustomAccordionDetails>
            </CustomAccordion>
        );
    }
);

/**
 * @typedef {Object} RubricOption
 * @property {string} id - Unique identifier for the grading option.
 * @property {number} rate - Numerical score or points for the option.
 * @property {string} title - Title of the grading option.
 * @property {string} description - Description of the grading option.
 * @property {boolean} [suggested] - Whether this option is suggested as the default.
 */

/**
 * @typedef {Object} RubricCriterion
 * @property {string} id - Unique identifier for the criterion.
 * @property {string} title - Title of the criterion.
 * @property {string} description - Description of the criterion.
 * @property {Array<RubricOption>} options - Array of grading options for this criterion.
 * @property {string} [justification] - Optional rationale or suggestion for the criterion (instructor review only).
 * @property {string} [gradingInstructions] - Optional detailed grading instructions for the criterion.
 */

/**
 * @typedef {Object} RubricForm
 * @property {Object.<string, string>} criteria - Maps criterion IDs to selected option IDs.
 * @property {Object.<string, string>} grading_explanations - Maps criterion IDs to grading explanation strings.
 */

/**
 * AccordionRubric is a component for displaying and interacting with grading rubrics in an accordion format.
 * It allows users to select grades for different criteria, override scores, and add grading explanations.
 * Each criterion is presented as an expandable accordion item with options to choose from.
 *
 * @param {boolean} [showGraded=false] - Whether to display the 'graded' tag and apply graded styling.
 * @param {boolean} [showGradingExplanationInput=false] - Whether to show the input for adding grading explanations.
 * @param {boolean} [disabled=false] - Whether the component is disabled and interactions are blocked.
 * @param {Array<RubricCriterion>} rubric - Array of rubric criteria objects.
 * @param {RubricForm} form - Form state object containing criteria selections and grading explanations.
 * @param {function} setForm - Function to update the form state.
 * @param {Object} gradeOverrides - Object containing grade override values for criteria.
 * @param {function} setGradeOverrides - Function to update the grade overrides.
 * @param {string|null} selectedCriterionId - ID of the currently selected (expanded) criterion.
 * @param {function} setSelectedCriterionId - Function to set the selected criterion ID.
 * @param {React.Ref} ref - Ref forwarded to the root Stack component.
 * @returns {React.ReactElement} The rendered AccordionRubric component.
 */
export default forwardRef(
    (
        {
            showGraded,
            showGradingExplanationInput,
            disabled,
            rubric,
            form,
            setForm,
            gradeOverrides,
            setGradeOverrides,
            selectedCriterionId,
            setSelectedCriterionId,
            handleCriterionNavigation,
            hideCriterionNavigation,
            locationData,
        },
        ref
    ) => {
        const { device } = useViewport();

        useEffect(() => {
            if (device === 'mobile') return;
            rubric
                .map(({ options }) => options)
                .flat()
                .filter(({ suggested }) => suggested)
                .map(({ id }) => id)
                .forEach((id) => {
                    const child = document.getElementById(`${id}-label`);
                    const parent = child.parentElement;
                    if (!parent) return;
                    const isParentScrollable = parent.scrollWidth > parent.clientWidth;
                    if (isParentScrollable) {
                        const scrollAmount = child.getBoundingClientRect().left - parent.getBoundingClientRect().left;
                        parent.scrollLeft += scrollAmount;
                    }
                });
        }, [device, rubric]);

        useEffect(() => {
            if (selectedCriterionId) {
                const index = rubric.findIndex(({ id }) => id === selectedCriterionId);
                if (index !== -1) {
                    const timeoutId = setTimeout(() => {
                        scrollElementIntoView(`rubric-criterion-${index}`);
                    }, 100);
                    return () => clearTimeout(timeoutId);
                }
            }
        }, [selectedCriterionId, rubric]);

        return (
            <RootStack ref={ref} spacing={4}>
                {rubric.map((criterion, index) => {
                    return (
                        <AccordionItem
                            key={criterion.id}
                            index={index}
                            disabled={disabled}
                            showGraded={showGraded}
                            showGradingExplanationInput={showGradingExplanationInput}
                            rubric={rubric}
                            criterion={criterion}
                            optionId={form.criteria[criterion.id]}
                            gradingExplanation={form.grading_explanations[criterion.id]}
                            setForm={setForm}
                            gradeOverrides={gradeOverrides}
                            setGradeOverrides={setGradeOverrides}
                            selectedCriterionId={selectedCriterionId}
                            setSelectedCriterionId={setSelectedCriterionId}
                            handleCriterionNavigation={handleCriterionNavigation}
                            hideCriterionNavigation={hideCriterionNavigation}
                            locationData={locationData}
                        />
                    );
                })}
            </RootStack>
        );
    }
);

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomAccordion = styled(Accordion)(({ theme, expanded, isGraded }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        scrollMarginTop: '56px',
        borderRadius: '16px !important',
        ...(isGraded && {
            borderColor: theme.palette.ensightful.success[700],
        }),
        ...(expanded &&
            !isGraded && {
                borderColor: theme.palette.ensightful.warning[700],
                outline: `2px solid ${theme.palette.ensightful.warning[700]}`,
            }),
    };
});

const CustomAccordionSummary = styled(AccordionSummary)(({ theme, isExpanded, isGraded }) => {
    return {
        alignItems: 'flex-start',
        gap: theme.spacing(2),
        borderRadius: isExpanded ? '16px 16px 0 0 !important' : '16px !important',
        padding: theme.spacing(3),
        background: isGraded ? theme.palette.ensightful.success[50] : theme.palette.ensightful.neutral[100],
        '&:hover': {
            background: isGraded ? theme.palette.ensightful.success[100] : theme.palette.ensightful.neutral[200],
        },
        '&:active': {
            background: isGraded ? theme.palette.ensightful.success[200] : theme.palette.ensightful.neutral[300],
        },
        '&:focus-visible': {
            backgroundColor: isGraded ? theme.palette.ensightful.success[50] : theme.palette.ensightful.neutral[100],
            outlineOffset: '-6px',
        },
        '& .MuiAccordionSummary-expandIconWrapper': {
            position: 'relative',
            bottom: '2px',
        },
        '& .MuiAccordionSummary-content': {
            flexDirection: 'column',
        },
    };
});

const FeedbackButton = styled(Button)(({ theme }) => {
    return {
        alignSelf: 'flex-start',
        marginTop: theme.spacing(2.5),
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
    };
});

const OverrideTextInput = styled(TextInput)(({ theme, isSuggestedScore }) => {
    return {
        width: '55px',
        '& > .Mui-disabled > input': {
            color: theme.palette.ensightful.neutral[900],
            '-webkitTextFillColor': 'unset',
        },
        ...(isSuggestedScore && {
            '& input.MuiInputBase-input': {
                color: theme.palette.ensightful.primary[500],
            },
        }),
    };
});

const CustomAccordionDetails = styled(AccordionDetails)(({ theme }) => {
    return {
        margin: 0,
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    };
});

const OverflowStack = styled(Stack)(({ theme, device }) => {
    return {
        flexDirection: device === 'mobile' ? 'column' : 'row',
        flexWrap: 'unset',
        overflowX: 'auto',
        clipPath: 'inset(0 round 16px)',
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomFormLabel = styled(FormLabel)(({ theme }) => {
    return {
        flex: 1,
        minWidth: '240px',
        minHeight: '100%',
        padding: theme.spacing(3),
        cursor: 'pointer',
    };
});

const CustomRadio = styled(Radio)(({ theme }) => {
    return {
        alignSelf: 'flex-start',
        margin: theme.spacing(-2),
    };
});

const GradingInstructionsAccordion = styled(Accordion)(() => {
    return {
        background: 'inherit',
    };
});

const GradingInstructionsAccordionSummary = styled(AccordionSummary)(({ theme }) => {
    return {
        width: 'fit-content',
        '& > div': {
            color: theme.palette.ensightful.primary[500],
        },
    };
});

const GradingInstructionsAccordionDetails = styled(AccordionDetails)(({ theme }) => {
    return {
        marginTop: theme.spacing(2),
    };
});
